import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Plus, Pencil, Trash2, FolderTree } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCategories = () => {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: "", name_ar: "", description: "", order: 0 });

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      toast.error(t("failedToFetchCategories"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      if (editing) {
        await axios.put(`${API}/categories/${editing.id}`, formData, { headers });
        toast.success(t("categoryUpdated"));
      } else {
        await axios.post(`${API}/categories`, formData, { headers });
        toast.success(t("categoryCreated"));
      }
      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToSaveCategory"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("deleteCategoryConfirm"))) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeaders() });
      toast.success(t("categoryDeleted"));
      fetchCategories();
    } catch (error) {
      toast.error(t("failedToDeleteCategory"));
    }
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setFormData({ name: cat.name, name_ar: cat.name_ar || "", description: cat.description || "", order: cat.order || 0 });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ name: "", name_ar: "", description: "", order: 0 });
  };

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="flex justify-between">
        <h2 className="text-xl font-heading font-semibold text-white">{t("categories")} ({categories.length})</h2>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#d4af37] text-[#0f172a]" data-testid="add-category-btn">
              <Plus className="mr-2 h-4 w-4" /> {t("addCategory")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? t("editCategory") : t("addCategory")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-slate-300">{t("name")} *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-slate-700 border-slate-600" data-testid="category-name" />
              </div>
              <div>
                <Label className="text-slate-300">{t("arabicName")}</Label>
                <Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label className="text-slate-300">{t("description")}</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label className="text-slate-300">{t("displayOrder")}</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} className="bg-slate-700 border-slate-600" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">{t("cancel")}</Button>
                <Button type="submit" className="bg-[#d4af37] text-[#0f172a]" data-testid="save-category-btn">{editing ? t("update") : t("create")}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">{t("loading")}</div>
          ) : categories.length === 0 ? (
            <div className="p-8 text-center">
              <FolderTree className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">{t("noCategories")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">{t("displayOrder")}</TableHead>
                  <TableHead className="text-slate-300">{t("name")}</TableHead>
                  <TableHead className="text-slate-300">{t("arabic")}</TableHead>
                  <TableHead className="text-slate-300">{t("description")}</TableHead>
                  <TableHead className="text-slate-300 text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((cat) => (
                  <TableRow key={cat.id} className="border-slate-700">
                    <TableCell className="text-white">{cat.order}</TableCell>
                    <TableCell className="text-white font-medium">{cat.name}</TableCell>
                    <TableCell className="text-slate-300">{cat.name_ar || "-"}</TableCell>
                    <TableCell className="text-slate-400">{cat.description || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(cat)} className="text-slate-400 hover:text-white">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCategories;
